import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, Suspense, lazy } from 'react';
import { supabase } from './supabase';
import { User } from '@supabase/supabase-js';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { trackMetaEvent } from './services/metaConversions';
import { EnhancedLoader } from './components/EnhancedLoader';
import { CommandPalette } from './components/CommandPalette';
import { QuickCompareOverlay } from './components/QuickCompareOverlay';
import { MobileNav } from './components/MobileNav';
import { SupportWidget } from './components/SupportWidget';
import { AnimatePresence, motion } from 'motion/react';
import { Toaster } from 'sonner';
import { checkAndToastGamificationUnlock } from './utils/gamification.js';

// Lazy loading all pages for site-wide code splitting optimization
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
        // Return a promise that never resolves while the page reloads
        return new Promise<any>(() => {});
      }
      throw error;
    }
  });

const Home = lazyWithRetry(() => import('./pages/Home'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Pricing = lazyWithRetry(() => import('./pages/Pricing'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const Compare = lazyWithRetry(() => import('./pages/Compare'));
const Rankings = lazyWithRetry(() => import('./pages/Rankings'));
const ValuationExplanation = lazyWithRetry(() => import('./pages/Education/ValuationExplanation'));
const GrahamExplanation = lazyWithRetry(() => import('./pages/Education/GrahamExplanation'));
const DCFExplanation = lazyWithRetry(() => import('./pages/Education/DCFExplanation'));
const ComoCalcularValuation = lazyWithRetry(() => import('./pages/Education/ComoCalcularValuation'));
const PeterLynchExplanation = lazyWithRetry(() => import('./pages/Education/PeterLynchExplanation'));
const ReverseValuationExplanation = lazyWithRetry(() => import('./pages/Education/ReverseValuationExplanation'));
const PiotroskiExplanation = lazyWithRetry(() => import('./pages/Education/PiotroskiExplanation'));
const AltmanZExplanation = lazyWithRetry(() => import('./pages/Education/AltmanZExplanation'));
const BeneishMExplanation = lazyWithRetry(() => import('./pages/Education/BeneishMExplanation'));
const Calculator = lazyWithRetry(() => import('./pages/Calculator'));
const CompoundInterest = lazyWithRetry(() => import('./pages/CompoundInterest'));
const Portfolio = lazyWithRetry(() => import('./pages/Portfolio'));
const Privacy = lazyWithRetry(() => import('./pages/Privacy'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const Cookies = lazyWithRetry(() => import('./pages/Cookies'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const Success = lazyWithRetry(() => import('./pages/Success'));
const Failure = lazyWithRetry(() => import('./pages/Failure'));
const Pending = lazyWithRetry(() => import('./pages/Pending'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const UpdatePassword = lazyWithRetry(() => import('./pages/UpdatePassword'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));

function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Standard pageview tracking
      if ((window as any).fbq) {
        (window as any).fbq('track', 'PageView');
      }
      if ((window as any).ttq) {
        (window as any).ttq.page();
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

import { useNetworkStatus } from './hooks/useNetworkStatus';

function NetworkStatusManager() {
  useNetworkStatus();
  return null;
}

function AnimatedRoutes({ session }: { session: User | null }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* We wrap routes in a motion.div to give standard page transitions */}
        <Route path="/login" element={!session ? <PageWrapper><Login /></PageWrapper> : <Navigate to="/" />} />
        <Route path="/reset-password" element={!session ? <PageWrapper><ResetPassword /></PageWrapper> : <Navigate to="/" />} />
        <Route path="/update-password" element={<PageWrapper><UpdatePassword /></PageWrapper>} />
        <Route path="/pricing" element={<PageWrapper><Pricing /></PageWrapper>} />
        <Route path="/success" element={<PageWrapper><Success session={session} /></PageWrapper>} />
        <Route path="/failure" element={<PageWrapper><Failure /></PageWrapper>} />
        <Route path="/pending" element={<PageWrapper><Pending /></PageWrapper>} />
        <Route path="/compare" element={<PageWrapper><Compare session={session} /></PageWrapper>} />
        <Route path="/portfolio" element={<PageWrapper><Portfolio session={session} /></PageWrapper>} />
        <Route path="/juros-compostos" element={<PageWrapper><CompoundInterest /></PageWrapper>} />
        <Route path="/rankings" element={<PageWrapper><Rankings session={session} /></PageWrapper>} />
        <Route path="/calculator" element={<PageWrapper><Calculator /></PageWrapper>} />
        <Route path="/privacidade" element={<PageWrapper><Privacy /></PageWrapper>} />
        <Route path="/termos" element={<PageWrapper><Terms /></PageWrapper>} />
        <Route path="/cookies" element={<PageWrapper><Cookies /></PageWrapper>} />
        <Route path="/contato" element={<PageWrapper><Contact /></PageWrapper>} />
        <Route path="/admin-dashboard-secret-crm" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
        <Route path="/profile" element={session ? <PageWrapper><Profile session={session} /></PageWrapper> : <Navigate to="/login" />} />
        <Route path="/educacao/o-que-e-valuation" element={<PageWrapper><ValuationExplanation /></PageWrapper>} />
        <Route path="/educacao/formula-de-graham" element={<PageWrapper><GrahamExplanation /></PageWrapper>} />
        <Route path="/educacao/fluxo-de-caixa-descontado" element={<PageWrapper><DCFExplanation /></PageWrapper>} />
        <Route path="/educacao/como-calcular-valuation" element={<PageWrapper><ComoCalcularValuation /></PageWrapper>} />
        <Route path="/educacao/metodo-peter-lynch" element={<PageWrapper><PeterLynchExplanation /></PageWrapper>} />
        <Route path="/educacao/valuation-reverso" element={<PageWrapper><ReverseValuationExplanation /></PageWrapper>} />
        <Route path="/educacao/piotroski-f-score" element={<PageWrapper><PiotroskiExplanation /></PageWrapper>} />
        <Route path="/educacao/altman-z-score" element={<PageWrapper><AltmanZExplanation /></PageWrapper>} />
        <Route path="/educacao/beneish-m-score" element={<PageWrapper><BeneishMExplanation /></PageWrapper>} />
        <Route path="/acao/:ticker" element={<PageWrapper><Home session={session} /></PageWrapper>} />
        <Route path="/" element={<PageWrapper><Home session={session} /></PageWrapper>} />
        <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
}

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

export default function App() {
  const [session, setSession] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syncedUsers = new Set<string>();
    const syncProfile = async (user: User) => {
      if (!user?.id || syncedUsers.has(user.id)) return;
      syncedUsers.add(user.id);
      try {
        const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
        
        let shouldUpdateAuth = false;
        const authUpdates: any = {};

        if (error && error.code === 'PGRST116') { // not found
          const { user_metadata } = user;
          
          const rootName = user.email ? user.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : ((user_metadata?.full_name?.split(' ')?.[0]?.toLowerCase()) || 'user');
          const defaultUsername = rootName + Math.floor(Math.random() * 100000).toString();
          
          const { error: upsertError } = await supabase.from('profiles').upsert({
            id: user.id,
            full_name: user_metadata?.full_name || user_metadata?.name || user.email?.split('@')[0] || '',
            username: user_metadata?.username || defaultUsername,
            avatar_url: user_metadata?.avatar_url || user_metadata?.picture || '',
            age: user_metadata?.age || null,
            city: user_metadata?.city || '',
            state: user_metadata?.state || '',
            phone: user_metadata?.phone || '',
          });
          
          if (!upsertError) {
            trackMetaEvent('CompleteRegistration');
            trackMetaEvent('Lead');
          }
          if (upsertError) console.error("Error creating profile:", upsertError);
        } else if (profile) {
          const isProfileAvatarGoogle = profile.avatar_url?.includes('googleusercontent.com');
          const isUserMetaAvatarGoogle = (user.user_metadata?.avatar_url || user.user_metadata?.picture)?.includes('googleusercontent.com');
          
          let profileUpdates: any = {};
          
          if (!profile.full_name && (user.user_metadata?.full_name || user.user_metadata?.name || user.email)) {
            profileUpdates.full_name = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '';
          }

          if (!profile.username) {
            const rootName = user.email ? user.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : ((user.user_metadata?.full_name?.split(' ')?.[0]?.toLowerCase()) || 'user');
            profileUpdates.username = rootName + Math.floor(Math.random() * 100000).toString();
          }

          const currentMetaAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture;

          if (profile.avatar_url && profile.avatar_url !== currentMetaAvatar) {
            if (!isProfileAvatarGoogle) {
                authUpdates.avatar_url = profile.avatar_url;
                shouldUpdateAuth = true;
            } else if (isProfileAvatarGoogle && isUserMetaAvatarGoogle) {
                profileUpdates.avatar_url = currentMetaAvatar;
            }
          } else if (!profile.avatar_url && currentMetaAvatar) {
            profileUpdates.avatar_url = currentMetaAvatar;
          }
          
          if (profile.full_name && profile.full_name !== (user.user_metadata?.full_name || user.user_metadata?.name)) {
             authUpdates.full_name = profile.full_name;
             shouldUpdateAuth = true;
          }

          if (profile.plan && profile.plan !== user.user_metadata?.plan) {
            authUpdates.plan = profile.plan;
            shouldUpdateAuth = true;
          }
          if (profile.subscription_status && profile.subscription_status !== user.user_metadata?.subscription_status) {
            authUpdates.subscription_status = profile.subscription_status;
            shouldUpdateAuth = true;
          }
          if (profile.plan_expires_at !== undefined && profile.plan_expires_at !== user.user_metadata?.plan_expires_at) {
            authUpdates.plan_expires_at = profile.plan_expires_at;
            shouldUpdateAuth = true;
          }
          
          if (!profile.age && user.user_metadata?.age) profileUpdates.age = user.user_metadata.age;
          if (!profile.city && user.user_metadata?.city) profileUpdates.city = user.user_metadata.city;
          if (!profile.state && user.user_metadata?.state) profileUpdates.state = user.user_metadata.state;
          if (!profile.phone && user.user_metadata?.phone) profileUpdates.phone = user.user_metadata.phone;

          if (Object.keys(profileUpdates).length > 0) {
            await supabase.from('profiles').update(profileUpdates).eq('id', user.id);
          }

          if (shouldUpdateAuth) {
            const { data: updatedUser } = await supabase.auth.updateUser({ data: authUpdates });
            if (updatedUser?.user) {
              setSession(updatedUser.user);
            }
          }
          
          let createdDate = profile.created_at ? new Date(profile.created_at) : new Date(user.created_at || Date.now());
          const diffTime = Math.abs(new Date().getTime() - createdDate.getTime());
          const accountDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          checkAndToastGamificationUnlock(user.id, 'loyalty', accountDays);
        }
      } catch (err) {
        syncedUsers.delete(user.id);
        console.error('Error syncing profile:', err);
      }
    };

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Error getting session:", error.message);
        if (error.message.includes('Refresh Token')) {
          supabase.auth.signOut().catch(console.error);
        }
      }
      if (session?.user) {
        syncProfile(session.user);
      }
      setSession(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        syncedUsers.clear();
        setSession(null);
      } else {
        if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
          syncProfile(session.user);
        }
        setSession(session?.user ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <EnhancedLoader />;
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <RouteTracker />
        <ScrollToTop />
        <div className="flex flex-col min-h-screen relative overflow-x-hidden">
          <Sidebar session={session} />
          <Header session={session} />
          <main className="flex-grow relative z-20 pb-20 md:pb-0">
            <Suspense fallback={<EnhancedLoader />}>
              <AnimatedRoutes session={session} />
            </Suspense>
          </main>
          <Footer />
          <MobileNav />
          <CommandPalette />
          <QuickCompareOverlay />
          <SupportWidget />
          <NetworkStatusManager />
          <Toaster position="bottom-right" richColors theme="system" closeButton />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}
